import { MasterType } from '../types/master';

/**
 * AIマスタ照合レコード数の上限チェック（#3288）。
 *
 * ユーザー企業とマスタ件数で月額料金が変わるため、事務局が企業ごとに設定した
 * 上限を超えるレコードはアップロードさせない。同じ企業に複数マスタがある場合は
 * 全マスタの合計レコード件数で判定する。
 *
 * 上限が未設定（本機能より前から存在する企業）の場合は制限なしとして扱う。
 */

export interface MasterRecordLimitCheckParams {
  /** 企業に登録済みのマスタ一覧（論理削除済みは含めない） */
  masters: MasterType[];
  /** 今回アップロードするCSVのデータ行数 */
  uploadingRowCount: number;
  /** 企業に設定されたレコード数上限。未設定なら制限なし */
  limit: number | undefined;
  /**
   * CSV更新で差し替える対象のマスタID。
   * 指定したマスタの既存行数は、置き換えられて消えるため合計から除外する。
   */
  excludeMasterId?: string;
}

export interface MasterRecordLimitCheckResult {
  /** 上限を超える場合 true */
  exceeded: boolean;
  /** 今回のアップロードを含めた合計レコード数 */
  totalRowCount: number;
}

/**
 * 登録済みマスタの合計レコード数を求める。
 * @param masters - 対象のマスタ一覧
 * @param excludeMasterId - 合計から除外するマスタID（CSV更新時の差し替え対象）
 */
export const sumMasterRowCount = (masters: MasterType[], excludeMasterId?: string): number =>
  masters
    .filter(master => master.master_id !== excludeMasterId)
    .reduce((total, master) => total + (master.row_count ?? 0), 0);

/**
 * アップロード後の合計レコード数が上限を超えるかを判定する。
 */
export const checkMasterRecordLimit = (
  params: MasterRecordLimitCheckParams,
): MasterRecordLimitCheckResult => {
  const { masters, uploadingRowCount, limit, excludeMasterId } = params;

  const totalRowCount = sumMasterRowCount(masters, excludeMasterId) + uploadingRowCount;

  // 上限未設定は制限なし扱い（既存企業を止めないため）
  if (limit === undefined || limit === null) {
    return { exceeded: false, totalRowCount };
  }

  return { exceeded: totalRowCount > limit, totalRowCount };
};

/**
 * 上限超過時にアップロード画面へ表示するエラーメッセージを組み立てる。
 */
export const buildMasterRecordLimitErrorMessage = (totalRowCount: number, limit: number): string =>
  `マスタの合計レコード数が上限を超えるためアップロードできません。（合計 ${totalRowCount.toLocaleString()} 件 / 上限 ${limit.toLocaleString()} 件）上限の変更をご希望の場合はお問い合わせください。`;
